// Core conversion logic for TIME Watch.
// 1 euro = 1 minute.

// Time units for the "Tempo Libero" display.
// year=525600, month=43200 (30d), day=1440, hour=60.
export const TIME_UNITS = {
  year: 525600,
  month: 43200,
  day: 1440,
  hour: 60,
};

// MITEL banknote denominations (exact, user-specified order).
export const MITEL_DENOMINATIONS = [
  525600, 42300, 10080, 1440, 60, 50, 40, 30, 20, 10, 5,
];

// Human labels for each denomination.
export const DENOMINATION_LABELS = {
  525600: "Anno",
  42300: "Mese",
  10080: "Settimana",
  1440: "Giorno",
  60: "Ora",
  50: "50",
  40: "40",
  30: "30",
  20: "20",
  10: "10",
  5: "5",
};

export function convertEuroToTime(euro) {
  const totalMinutes = Math.max(0, Math.floor(Number(euro) || 0));
  let remaining = totalMinutes;

  const years = Math.floor(remaining / TIME_UNITS.year);
  remaining -= years * TIME_UNITS.year;

  const months = Math.floor(remaining / TIME_UNITS.month);
  remaining -= months * TIME_UNITS.month;

  const days = Math.floor(remaining / TIME_UNITS.day);
  remaining -= days * TIME_UNITS.day;

  const hours = Math.floor(remaining / TIME_UNITS.hour);
  remaining -= hours * TIME_UNITS.hour;

  const minutes = remaining;

  return { years, months, days, hours, minutes, totalMinutes };
}

export function formatTimeDisplay({ years, months, days, hours, minutes }) {
  const y = String(years).padStart(4, "0");
  const m = String(months).padStart(2, "0");
  const d = String(days).padStart(2, "0");
  const h = String(hours).padStart(2, "0");
  const min = String(minutes).padStart(2, "0");
  return `${y}.${m}.${d}.${h}.${min}`;
}

// Alias: reuse the same integer-minutes-to-parts split
// for TEMPO PER LASCIARE IL SEGNO / TEMPO DATO / TEMPO AVUTO.
export function minutesToTimeParts(totalMinutes) {
  return convertEuroToTime(totalMinutes);
}

// Minutes remaining from now until (DOB + 100 years).
// dobStr is a "YYYY-MM-DD" string. Returns null if invalid, 0 if reached.
export function minutesUntilCentenary(dobStr) {
  if (!dobStr) return null;
  const [y, m, d] = dobStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dob = new Date(y, m - 1, d, 0, 0, 0, 0);
  if (isNaN(dob.getTime())) return null;
  const target = new Date(dob);
  target.setFullYear(dob.getFullYear() + 100);
  const now = new Date();
  if (target <= now) return 0;
  return Math.floor((target - now) / 60000);
}

/**
 * Google-Sheets-compatible DATEDIF (Y / YM / MD) between two Date objects.
 * Returns { years, months, days } computed on a calendar basis:
 *  - real days per month (28/29/30/31)
 *  - real leap years (Feb 29)
 *  - no fixed 30-day months, no 365-day years
 *
 * Semantics match =DATEDIF(start, end, "Y"), "YM", "MD".
 * If end <= start returns zeros.
 */
export function datedifYMD(startDate, endDate) {
  if (
    !(startDate instanceof Date) ||
    !(endDate instanceof Date) ||
    isNaN(startDate.getTime()) ||
    isNaN(endDate.getTime()) ||
    endDate <= startDate
  ) {
    return { years: 0, months: 0, days: 0 };
  }
  const sy = startDate.getFullYear();
  const sm = startDate.getMonth() + 1; // 1..12
  const sd = startDate.getDate();
  const ey = endDate.getFullYear();
  const em = endDate.getMonth() + 1;
  const ed = endDate.getDate();

  let years = ey - sy;
  let months = em - sm;
  let days = ed - sd;

  if (days < 0) {
    // borrow from previous calendar month of end (real days count)
    months -= 1;
    // last day of the month before `em` in year `ey`
    days += new Date(ey, em - 1, 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years, months, days };
}

/**
 * PMV — anni/mesi/giorni residui fino al centenario del DOB, calcolati con
 * la stessa logica di Google Sheets (=DATEDIF calendariale).
 *
 * Confronto date-only (00:00 locale) come fa Google Sheets, quindi il valore
 * cambia una sola volta al giorno (a mezzanotte locale).
 *
 * @param {string} dobStr — "YYYY-MM-DD"
 * @returns {{years:number, months:number, days:number} | null}
 */
export function pmvYearsMonthsDaysUntilCentenary(dobStr) {
  if (!dobStr) return null;
  const [y, m, d] = dobStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dob = new Date(y, m - 1, d, 0, 0, 0, 0);
  if (isNaN(dob.getTime())) return null;
  // target = same day/month, +100 years (local timezone, date-only)
  const target = new Date(dob.getFullYear() + 100, dob.getMonth(), dob.getDate(), 0, 0, 0, 0);
  // today at 00:00 local (drop time-of-day, like Google Sheets TODAY())
  const n = new Date();
  const today = new Date(n.getFullYear(), n.getMonth(), n.getDate(), 0, 0, 0, 0);
  if (target <= today) return { years: 0, months: 0, days: 0 };
  return datedifYMD(today, target);
}

/**
 * Estensione di pmvYearsMonthsDaysUntilCentenary con ore/minuti coerenti.
 *
 * La parte y/m/d è IDENTICA a quella mostrata dal TIME Watch (single source
 * of truth). Le ore/minuti rappresentano la porzione residua del giorno
 * corrente prima della prossima mezzanotte locale — cioè quanto manca alla
 * prossima transizione del contatore "giorni". Questa scelta preserva la
 * coerenza y/m/d con TIME Watch e aggiunge la precisione richiesta
 * dall'Area Personale (anni · mesi · giorni · ore · minuti) senza cambiare
 * il calcolo originale.
 *
 * @param {string} dobStr
 * @returns {{years:number, months:number, days:number, hours:number, minutes:number} | null}
 */
export function pmvUntilCentenaryYMDHM(dobStr) {
  const ymd = pmvYearsMonthsDaysUntilCentenary(dobStr);
  if (!ymd) return null;
  const now = new Date();
  // Minutes remaining until next local midnight.
  const minutesToNextMidnight =
    (24 * 60 - (now.getHours() * 60 + now.getMinutes())) % (24 * 60);
  const hours = Math.floor(minutesToNextMidnight / 60);
  const minutes = minutesToNextMidnight % 60;
  return { ...ymd, hours, minutes };
}

// Seconds remaining from now until today's 20:00. 0 after 20:00.
export function secondsUntilEightPm() {
  const now = new Date();
  const target = new Date(now);
  target.setHours(20, 0, 0, 0);
  if (now >= target) return 0;
  return Math.floor((target - now) / 1000);
}

// Split total seconds into { h, m, s }.
export function splitHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  return {
    h: Math.floor(s / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
}

// Format H, M, S as "HH:MM:SS".
export function formatHMS({ h, m, s }) {
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

// Format only years.months.days with 3-digit years (used by ORIZZONTE).
export function formatYmdDisplay({ years, months, days }) {
  const y = String(years).padStart(3, "0");
  const m = String(months).padStart(2, "0");
  const d = String(days).padStart(2, "0");
  return `${y}.${m}.${d}`;
}

// Greedy MITEL wallet decomposition.
// Returns [{ denom, count }] and { residual } minutes < smallest denom (5).
export function computeMitelWallet(euro) {
  const totalMinutes = Math.max(0, Math.floor(Number(euro) || 0));
  let remaining = totalMinutes;
  const notes = [];
  for (const d of MITEL_DENOMINATIONS) {
    const count = Math.floor(remaining / d);
    if (count > 0) {
      notes.push({ denom: d, count });
      remaining -= count * d;
    }
  }
  return { notes, residual: remaining, totalMinutes };
}

// Nicely format the caption "16 da 60 + 2 da 20".
export function formatMitelSummary(notes) {
  if (notes.length === 0) return "—";
  return notes.map((n) => `${n.count} da ${formatInt(n.denom)}`).join(" + ");
}

// Format a plain integer with Italian thousands separator (dot).
// Always groups every 3 digits (including 4-digit numbers like 1.440).
export function formatInt(n) {
  return String(Math.trunc(Number(n) || 0)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    "."
  );
}
